/**
 * Helpers extraídos de `WeekView.tsx` para satisfacer Vite Fast Refresh
 * (regla `react-refresh/only-export-components`): un archivo de componente
 * solo debe exportar componentes. La lógica pura va aparte.
 */
import type { TrainingSession } from "@/types/api";

/** Detecta sustitutas por fatiga en una colección de sesiones.
 *
 * Sprint 10 S10.2b: cuando el atleta reporta fatiga, el backend crea
 * una SEGUNDA sesión en el mismo `session_date` (sub `rod_z2_corto`
 * planned) además de marcar la original `skipped` con
 * `skip_reason='fatigue'`. Esta función detecta el patrón 100%
 * client-side a partir del shape ya serializado por el backend.
 *
 * Heurística:
 *   Para cada session_date con >= 2 sesiones:
 *     - Si existe AL MENOS UNA con `completion_status='skipped'` y
 *       `skip_reason='fatigue'` (la "original" reportada).
 *     - Y existe OTRA distinta con `completion_status='planned'`.
 *   → la segunda se marca como `isFatigueSubstitute`.
 *
 * Devuelve un `Set<number>` con los IDs de las sesiones que son
 * sustitutas (para evitar re-cálculo por render).
 */
export function _detectFatigueSubstituteIds(
  sessions: TrainingSession[],
): Set<number> {
  const byDate = new Map<string, TrainingSession[]>();
  for (const s of sessions) {
    const bucket = byDate.get(s.session_date) ?? [];
    bucket.push(s);
    byDate.set(s.session_date, bucket);
  }
  const subIds = new Set<number>();
  for (const dateSessions of byDate.values()) {
    if (dateSessions.length < 2) continue;
    const hasFatigueSkipped = dateSessions.some(
      (s) =>
        s.completion_status === "skipped" && s.skip_reason === "fatigue",
    );
    if (!hasFatigueSkipped) continue;
    for (const s of dateSessions) {
      // Las "planned" del mismo día que la fatigue-skipped son las subs.
      // No marcamos la skipped propia (ya tiene su propio badge vía
      // skip_reason).
      if (s.completion_status === "planned") {
        subIds.add(s.id);
      }
    }
  }
  return subIds;
}
